// @ts-strict-ignore
import styled, { css, keyframes } from 'styled-components';

import type { Action } from 'flow/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import { ContainerType } from './components/NodeEditorDrawer/ThresholdsTab/types';

import { getOutcomeBaseColor } from '../service/actions/outcomes/outcomes';

export function getActionBorderBottom(action: Action) {
  switch (action?.type) {
    case ActionType.OUTCOME: {
      return `2px solid ${getOutcomeBaseColor(action.outcome.name)}`;
    }
    case ActionType.OUTCOME_REASON: {
      let { color } = action.outcomeReason;
      if (!color) color = '000000';
      const prefix = color[0] === '#' ? '' : '#';
      return `2px solid ${prefix}${color}`;
    }
    case ActionType.TAG: {
      const { color } = action.tag;
      const prefix = color[0] === '#' ? '' : '#';
      return `2px solid ${prefix}${color}`;
    }
  }

  return '2px solid transparent';
}

/* Generates text styles to feed into a styled Action component
 */
export function getActionTextStyles(action: Action) {
  switch (action.type) {
    case ActionType.SERVICE: {
      return `
        color: var(--green-green-550, #3A795F);
      `;
    }
    case ActionType.OUTPUT:
    case ActionType.INPUT:
    case ActionType.MATRIX_MODEL: {
      return `
        color: var(--cl-juicyBlue-500);
      `;
    }
    case ActionType.SNIPPET: {
      return `
        color: var(--cl-purple-600);
      `;
    }
  }
  return '';
}

/* This is used to wrap a StyledAction component in order to highlight the component
 * when a user mouses over this wrapper. Allows us to separate the necessary action styles from
 * the necessary wrapper styles
 */
export const StyledActionWrapper = styled.span`
  border-color: 'transparent';
  box-shadow: none;
  cursor: pointer;
  display: inline-block;
  margin-top: var(--cl-space-2);
  padding: var(--cl-space-0);
  width: auto;

  &:first-child {
    margin-top: 0px;
  }
`;

/* This represents an Action in text:
 *   - Underline with specific tag color for tags
 *   - Bold and green for services
 *   - Bold and blue for input attributes, output attributes, matrix models
 *   - Bold and outcome color for outcomes
 */
export const StyledAction = styled.span<{
  action: Action;
}>`
  border-bottom: ${props => getActionBorderBottom(props.action)};
  ${props => getActionTextStyles(props.action)}
  display: inline;
  word-break: break-word;
`;

export const StyledActionExtended = styled(StyledAction)`
  border-radius: var(--cl-radius-1);
  font-family: var(--cl-font-family-mono);
  font-size: var(--cl-font-size-2);
  font-weight: var(--cl-font-weight-regular);
  line-height: var(--cl-line-height-4);
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: var(--cl-yellow-100);
    border-color: 'transparent';
  }
`;

export const StyledCodeStyleText = styled.span`
  font-family: var(--cl-font-family-mono);
  font-size: var(--cl-font-size-2);
  font-weight: var(--cl-font-weight-regular);
  margin-bottom: 0;
  margin-top: 0;
  flex-shrink: 0;
  word-break: break-word;
`;

export const StyledPre = styled.pre<{
  highlighted?: boolean;
  containerType?: ContainerType;
}>`
  font-family: var(--cl-font-family-mono);
  font-size: var(--cl-font-size-2);
  font-weight: var(--cl-font-weight-regular);
  margin-bottom: 0;
  margin-top: 0;
  flex-shrink: 0;

  ${props => (props.highlighted ? 'background-color: var(--cl-yellow-100);' : '')}
  transition: all 0.2s ease-in-out;
  overflow-x: visible;
  /* pre elements already have a padding bottom by default, so not setting one actually sets one */
  ${props => (props.containerType === ContainerType.TOOLTIP ? `
    white-space: pre-wrap;
    margin-bottom: 0;
    height: 100%;
    width: 100%;
  ` : '')}
  span {
    margin-top: var(--cl-space-1);
  }
`;

export const StyledReadOnlyLabel = styled.div`
  margin-right: var(--cl-space-1);
`;

export const blinkingAnimation = keyframes`
  0% {
    opacity: 0.2;
  }
  50% {
    opacity: .75;
  }
  75% {
    opacity: 1;
  }
  100% {
    opacity: .5;
  }
`;

export const HoverableSelectContainer = styled.div<{
  parenthesis: '(' | ')';
  isDropdownFocused: boolean;
  blinkGhostParenthesis: boolean;
  inAndOrRuleSet: boolean;
}>`
  display: inline-block;
  position: relative;
  box-sizing: border-box;
  margin-top: ${({ inAndOrRuleSet }) => (inAndOrRuleSet ? '-5px' : '0')};
  vertical-align: top;
  z-index: 1;

  &:after {
    content: '${({ parenthesis }) => parenthesis}';
    position: absolute;
    top: 0;
    left: 0;
    padding: var(--cl-space-1-5) 0 var(--cl-space-1) var(--cl-space-1);
    border-radius: var(--cl-radius-1);
    opacity: ${({ blinkGhostParenthesis }) => (blinkGhostParenthesis ? '1' : '0')};
    animation: ${({ blinkGhostParenthesis }) => (blinkGhostParenthesis
    ? css`${blinkingAnimation} 2000ms ease infinite`
    : 'none'
  )};
    transition: all 200ms ease;
  }

  &:hover:after {
    opacity: 1;
    background-color: var(--cl-yellow-100);
  }

  ${({ isDropdownFocused }) => isDropdownFocused && css`
    &:after {
      opacity: 1;
    }

    &:hover:after {
      background-color: var(--cl-yellow-200);
    }
  `}
`;

export const BlinkingPendingGroupParenthesis = styled.span`
  padding: var(--cl-space-1-5) 0 var(--cl-space-1-5) var(--cl-space-1);
  position: relative;
  display: inline-block;
  animation: ${blinkingAnimation} 2000ms ease infinite;
`;

export const Spacer = styled.div`
  display: inline-block;
  margin-left: -2px;
`;

export const InlineWrapper = styled.div<{ isPreview?: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  position: relative;
  width: fit-content;

  // Handles greying out preview linking thresholds on node link hover in the threshold
  // panel rather than adding conditional styles to all threshold text components
  ${({ isPreview }) => isPreview && css`
    && div,
      input,
      span {
        color: var(--cl-gray-700);
        font-weight: var(--cl-font-weight-regular);
      }
  `}
`;

export const IsolatedEditorContainer = styled.div<{
  indentLevel?: number;
}>`
  display: flex;
  flex-direction: column;
  gap: var(--cl-space-1);
  padding: var(--cl-space-2);
  background-color: white;
  box-sizing: content-box;
  box-shadow: 0px 0px 0px 4px var(--cl-juicyBlue-200);
  border-radius: var(--cl-radius-3);
  border: 1px solid var(--cl-juicyBlue-600);
  margin: var(--cl-space-3) var(--cl-space-5) var(--cl-space-3) var(--cl-space-1-5);
  margin-left: ${({ indentLevel }) => (indentLevel ? `${indentLevel * 20}px` : 'var(--cl-space-14)')};
  width: ${({ indentLevel }) => (
    // Full width, minus rule line left padding, minus any indent amount
    `calc(100% - 96px - ${(indentLevel ?? 0) * 20}px)`
  )};
`;
